import axios from 'axios';

// Configuration de base de l'API
const API_URL = process.env.REACT_APP_API_URL || '/api';

// Instance axios configurée
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token d'authentification
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expiré ou invalide
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Services API

// Authentification
export const authService = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },
};

// Membres
export const membresService = {
  getAll: (params = { limit: 100 }) => api.get('/membres', { params }),
  getById: (id) => api.get(`/membres/${id}`),
  create: (data) => api.post('/membres', data),
  update: (id, data) => api.put(`/membres/${id}`, data),
  delete: (id) => api.delete(`/membres/${id}`),
  getStats: (id) => api.get(`/membres/${id}/stats`),
  uploadPhoto: (id, formData) => {
    return api.post(`/membres/${id}/photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getEnAttente: () => api.get('/membres/en-attente'),
  validerMembre: (id) => api.put(`/membres/${id}/valider`),
  rejeterMembre: (id, raison) => api.put(`/membres/${id}/rejeter`, { raison }),

};

// Cotisations
export const cotisationsService = {
  getAll: () => api.get('/cotisations'),
  getByMembre: (membreId) => api.get(`/cotisations/membre/${membreId}`),
  create: (data) => api.post('/cotisations', data),
  update: (id, data) => api.put(`/cotisations/${id}`, data),
  delete: (id) => api.delete(`/cotisations/${id}`),
  getByType: (type) => api.get(`/cotisations/type/${type}`),
};

// Tontines
export const tontinesService = {
  getAll: () => api.get('/tontines'),
  getById: (id) => api.get(`/tontines/${id}`),
  create: (data) => api.post('/tontines', data),
  update: (id, data) => api.put(`/tontines/${id}`, data),
  delete: (id) => api.delete(`/tontines/${id}`),
  getVersements: (id) => api.get(`/tontines/${id}/versements`),
  addVersement: (id, data) => api.post(`/tontines/${id}/versements`, data),
};

// Assemblées
export const assembleesService = {
  getAll: (params) => api.get('/assemblees', { params }),
  getById: (id) => api.get(`/assemblees/${id}`),
  create: (data) => api.post('/assemblees', data),
  update: (id, data) => api.put(`/assemblees/${id}`, data),
  delete: (id) => api.delete(`/assemblees/${id}`),
  getParticipations: (id) => api.get(`/assemblees/${id}/participations`),
  confirmerPresence: (id, data) => api.post(`/assemblees/${id}/participation`, data),
  // presences = [{ membre_id, present }]
  marquerPresences: (id, presences) => api.post(`/assemblees/${id}/presence`, { presences }),
  cloturer: (id) => api.post(`/assemblees/${id}/cloturer`),
};

// Aides
export const aidesService = {
  getAll: () => api.get('/aides'),
  getByMembre: (membreId) => api.get(`/aides/membre/${membreId}`),
  create: (data) => api.post('/aides', data),
  update: (id, data) => api.put(`/aides/${id}`, data),
  delete: (id) => api.delete(`/aides/${id}`),
  approve: (id) => api.put(`/aides/${id}/approve`),
  reject: (id, reason) => api.put(`/aides/${id}/reject`, { reason }),
};

// Dashboard
export const dashboardService = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentActivity: () => api.get('/dashboard/recent-activity'),
};

// Comptes-rendus
export const comptesRendusService = {
  getAll: () => api.get('/comptes-rendus'),
  getById: (id) => api.get(`/comptes-rendus/${id}`),
  create: (data) => api.post('/comptes-rendus', data),
  update: (id, data) => api.put(`/comptes-rendus/${id}`, data),
  delete: (id) => api.delete(`/comptes-rendus/${id}`),
  validate: (id) => api.put(`/comptes-rendus/${id}/valider`),
  downloadPDF: (id) => api.get(`/comptes-rendus/${id}/pdf`, { responseType: 'blob' }),
};

// Notifications WhatsApp
export const whatsappService = {
  sendNotification: (data) => api.post('/whatsapp/send', data),
  sendBulk: (notifications) => api.post('/whatsapp/send-bulk', { notifications }),
};

// Blog
export const blogService = {
  getAll: (params) => api.get('/blog', { params }),
  getById: (id) => api.get(`/blog/${id}`),
  getBySlug: (slug) => api.get(`/blog/slug/${slug}`),
  create: (data) => api.post('/blog', data),
  update: (id, data) => api.put(`/blog/${id}`, data),
  delete: (id) => api.delete(`/blog/${id}`),
  publish: (id) => api.put(`/blog/${id}/publish`),
  
  uploadMedia: (formData) => {
    return api.post('/blog/upload-media', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

 export const commentairesService = {
   getByArticle: (articleId) => api.get(`/commentaires/${articleId}`),
   create: (data) => api.post('/commentaires', data),
   delete: (id) => api.delete(`/commentaires/${id}`),
   update: (id, data) => api.put(`/commentaires/${id}`, data),
 };

// Dépenses
export const depensesService = {
  getAll: () => api.get('/depenses'),
  getById: (id) => api.get(`/depenses/${id}`),
  create: (data) => api.post('/depenses', data),
  update: (id, data) => api.put(`/depenses/${id}`, data),
  delete: (id) => api.delete(`/depenses/${id}`),
};

// Rapports
export const rapportsService = {
  getFinancier: (params) => api.get('/rapports/financier', { params }),
  getCotisations: (params) => api.get('/rapports/cotisations', { params }),
  getPresences: (params) => api.get('/rapports/presences', { params }),
  exportPDF: (type, params) => api.get(`/rapports/export/${type}/pdf`, { params, responseType: 'blob' }),
  exportExcel: (type, params) => api.get(`/rapports/export/${type}/excel`, { params, responseType: 'blob' }),
};

// Enfants des membres (répertoire central — Excellence Scolaire)
export const enfantsService = {
  getByMembre: (membreId) => api.get(`/enfants/membre/${membreId}`),
  getById: (id) => api.get(`/enfants/${id}`),
  desactiver: (id) => api.delete(`/enfants/${id}`),
};

export default api;
